import { Popover, PopoverContent } from "@/kit/ui/popover";
import { AutoCompletePortalProps, AutoCompleteProps } from "./props";
import { PopoverPortal } from "@radix-ui/react-popover";
import {
  ComponentPropsWithoutRef,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useTransition,
} from "react";
import {
  Context,
  ListHandle,
  ListState,
  Unselected,
  useAutoComplete,
} from "./common";
import {
  Signal,
  TransitionSignal,
  useSignalCleanup,
  useSignalValue,
} from "@/utils/signal";

export function AutoComplete<T>({ children, ...props }: AutoCompleteProps<T>) {
  const [inTransition, startTransition] = useTransition();
  const list = useRef<ListHandle | null>(null);
  const props_ = useRef(props);
  props_.current = props;
  const context = useMemo<Context<T>>(
    () => ({
      open: new TransitionSignal(false, startTransition),
      loading: new Signal(false),
      input: new Signal(""),
      selected: new Signal<ListState<T>>({
        index: undefined,
        value: Unselected,
      }),
      list,

      onSelectItem(item) {
        props_.current.onSelectItem(item, context);
      },
      onPopItem() {
        props_.current.onPopItem(context);
      },

      reset() {
        context.open.value = false;
        context.loading.value = false;
        context.input.value = "";
        context.selected.value = { index: undefined, value: Unselected };
      },
    }),
    [startTransition, list, props_],
  );

  const { open, loading, input, selected } = context;
  useSignalCleanup(open, loading, input, selected);

  useEffect(() => {
    loading.value = inTransition;
  }, [loading, inTransition]);

  useEffect(() => {
    return open.subscribe((isOpen) => {
      if (!isOpen) {
        list.current?.reset();
      }
    });
  }, [open, list]);

  const isOpen = useSignalValue(open);

  return (
    <Context.Provider value={context as Context}>
      <Popover
        open={isOpen}
        onOpenChange={(hasOpened) => {
          console.log("popover", { hasOpened });
          open.dispatch(hasOpened);
        }}
      >
        {children}
      </Popover>
    </Context.Provider>
  );
}

export function AutoCompletePortal(props: AutoCompletePortalProps) {
  const { loading: loading_ } = useAutoComplete();
  const isLoading = useSignalValue(loading_);
  return (
    <PopoverPortal>
      <PopoverContent
        {...props}
        aria-busy={isLoading}
        align="start"
        onOpenAutoFocus={preventDefault}
        onCloseAutoFocus={preventDefault}
        onInteractOutside={preventDefault}
        onFocusOutside={preventDefault}
        onPointerDownOutside={preventDefault}
        onPointerDownCapture={preventDefault}
        onEscapeKeyDown={preventDefault}
        // Force the use of full size
        // https://www.radix-ui.com/primitives/docs/components/popover#content
        className="w-(--radix-popper-anchor-width)! max-w-none p-1"
      />
    </PopoverPortal>
  );
}

function preventDefault(evt: Event | SyntheticEvent) {
  evt.preventDefault();
}

export function LoadingAnchor(props: ComponentPropsWithoutRef<"div">) {
  const { loading: loading_ } = useAutoComplete();
  const isLoading = useSignalValue(loading_);
  return <div {...props} aria-busy={isLoading} />;
}
